"use client";

import { useRef } from "react";
import { useSwitch } from "@react-aria/switch";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import { useToggleState } from "@react-stately/toggle";

import { csx } from "../../utils";

import "./Switch.css";

export interface SwitchProps {
  /**
   * Whether the Switch is in the 'on' state (uncontrolled).
   */
  defaultOn?: boolean;

  /**
   * Whether the Switch is in the 'on' state. Providing this prop causes the
   * component to become controlled.
   */
  isOn?: boolean;

  /**
   * Should the input be non-interactive?
   */
  isDisabled?: boolean;

  /**
   * Called when the Switch's on state changes.
   */
  onChange?: (isOn: boolean) => void;

  /**
   * The string used for the input label. Forwarded to `aria-label`.
   */
  label?: string;

  /**
   * The text or component to be rendered as the Switch's description.
   */
  children?: React.ReactNode;
}

/**
 * A toggle switch component for boolean state control with support for controlled and uncontrolled modes.
 *
 * API:
 *  - {@link SwitchProps}
 */
export function Switch(props: SwitchProps) {
  let sanitizedProps = {
    ...props,
    isSelected: props.isOn,
    "data-testid": undefined,
  };

  let ref = useRef(null);

  let state = useToggleState(sanitizedProps);
  let { inputProps } = useSwitch(sanitizedProps, state, ref);

  return (
    <label
      className={csx(
        "proton-Switch",
        props.isDisabled && "proton-Switch--disabled",
        state.isSelected && "proton-Switch--on"
      )}
      data-on={state.isSelected || undefined}
      data-disabled={props.isDisabled || undefined}
      data-testid={props["data-testid"] || undefined}
      aria-label={props.label || undefined}
    >
      <VisuallyHidden>
        <input {...inputProps} ref={ref} />
      </VisuallyHidden>
      {props.label && <div className="proton-Switch__label">{props.label}</div>}
      <div className="proton-Switch__wrapper">
        <div className="proton-Switch__toggle">
          <div className="proton-Switch__slider"></div>
        </div>
        {props.children && (
          <div className="proton-Switch__description">{props.children}</div>
        )}
      </div>
    </label>
  );
}
